import React, { useEffect, useState, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Table,
  Image,
  Spinner,
  Modal,
} from "react-bootstrap";
import { FaUpload, FaPlus, FaImages, FaEdit, FaTrash } from "react-icons/fa";

const COLORS = {
  blue: "#0062A0",
  magenta: "#E91E63",
  lightBg: "#f4f7f9",
};

const API_URL = "https://radheyelectrical.com/api/carousel_api.php";

const CarouselAdmin = () => {
  const [carousel, setCarousel] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false); // Modal state
  const fileInputRef = useRef(null);
  const [editId, setEditId] = useState(null); // Track if editing
  const [form, setForm] = useState({
    badge: "",
    title: "",
    subtitle: "",
    description: "",
    sort_order: 0,
  });

  const [image, setImage] = useState(null);

  // Modal Handlers
  const handleClose = () => {
    setShowModal(false);
    resetForm();
  };
  const handleShow = () => setShowModal(true);

  const resetForm = () => {
    setForm({
      badge: "",
      title: "",
      subtitle: "",
      description: "",
      sort_order: 0,
    });
    setImage(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const fetchCarousel = async () => {
    try {
      const res = await fetch(API_URL);
      const data = await res.json();
      if (data.status === "success") {
        setCarousel(data.data || []);
      }
    } catch (err) {
      console.error("Fetch error:", err);
    }
  };

  useEffect(() => {
    fetchCarousel();
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleEdit = (item) => {
    setEditId(item.id);
    setForm({
      badge: item.badge || "",
      title: item.title || "",
      subtitle: item.subtitle || "",
      description: item.description || "",
      sort_order: item.sort_order || 0,
      existing_image: item.image_url, // Store existing URL
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this slide?")) return;

    try {
      const res = await fetch(`${API_URL}?id=${id}`, { method: "DELETE" });
      const data = await res.json();
      if (data.status === "success") {
        fetchCarousel();
      } else {
        alert(data.message);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData();
    Object.keys(form).forEach((key) => formData.append(key, form[key]));
    if (image) formData.append("image", image);
    if (editId) formData.append("id", editId); // Send ID for Update logic

    try {
      const res = await fetch(API_URL, { method: "POST", body: formData });
      const data = await res.json();
      if (data.status === "success") {
        handleClose();
        fetchCarousel();
        setEditId(null);
      } else {
        alert(data.message);
      }
    } catch (err) {
      alert("Error saving data");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container
      fluid
      style={{
        // background: COLORS.lightBg,
        minHeight: "100vh",
        // padding: "30px",
      }}
    >
      {/* HEADER SECTION: Title Left, Button Right */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 style={{ color: COLORS.blue, fontWeight: "bold", margin: 0 }}>
          <FaImages className="me-2" /> Homapage Slider{" "}
        </h3>
        <Button
          onClick={handleShow}
          style={{
            background: COLORS.blue,
            border: "none",
            padding: "10px 20px",
          }}
        >
          <FaPlus /> Add Carousel Slide
        </Button>
      </div>

      <Row>
        <Col md={12}>
          <Card className="shadow-sm border-0">
            <Card.Header style={{ background: COLORS.blue, color: "white" }}>
              Current Active Slides
            </Card.Header>
            <Card.Body>
              <Table responsive striped hover verticalAlign="middle">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: "80px" }}>Sort</th>
                    <th style={{ width: "120px" }}>Preview</th>
                    <th>Title</th>
                    <th>Badge</th>
                    <th>Subtitle</th>
                    <th>Actions</th> {/* New Column */}
                  </tr>
                </thead>
                <tbody>
                  {carousel.length > 0 ? (
                    carousel.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <strong>{item.sort_order}</strong>
                        </td>
                        <td>
                          {item.image_url ? (
                            <Image
                              src={item.image_url}
                              width={80}
                              height={50}
                              style={{
                                objectFit: "cover",
                                border: "1px solid #ddd",
                              }}
                              rounded
                            />
                          ) : (
                            <small className="text-muted">No Image</small>
                          )}
                        </td>
                        <td>{item.title}</td>
                        <td>
                          <span className="badge bg-info text-dark">
                            {item.badge}
                          </span>
                        </td>
                        <td className="text-muted">{item.subtitle}</td>
                        <td>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="me-2"
                            onClick={() => handleEdit(item)}
                          >
                            <FaEdit />
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDelete(item.id)}
                          >
                            <FaTrash />
                            {/* {item.id} */}
                          </Button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center py-4">
                        No slides found in the database.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* MODAL FOR INPUT FORM */}
      <Modal show={showModal} onHide={handleClose} centered size="lg">
        <Modal.Header
          closeButton
          style={{ background: COLORS.blue, color: "white" }}
        >
          <Modal.Title>
            {editId ? <FaEdit /> : <FaPlus />}{" "}
            {editId ? "Edit Slide" : "Add New Slide"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Badge</Form.Label>
                  <Form.Control
                    name="badge"
                    value={form.badge}
                    onChange={handleChange}
                    placeholder="e.g. Limited Offer"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Sort Order</Form.Label>
                  <Form.Control
                    type="number"
                    name="sort_order"
                    value={form.sort_order}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                name="title"
                value={form.title}
                onChange={handleChange}
                required
                placeholder="Main heading of the slide"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Subtitle</Form.Label>
              <Form.Control
                name="subtitle"
                value={form.subtitle}
                onChange={handleChange}
                placeholder="Secondary heading"
              />
            </Form.Group>

            {/* <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={form.description}
                onChange={handleChange}
              />
            </Form.Group> */}

            <Form.Group className="mb-3">
              <Form.Label>Upload Image</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                ref={fileInputRef}
                onChange={(e) => setImage(e.target.files[0])}
                required
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              style={{
                background: COLORS.magenta,
                border: "none",
                minWidth: "120px",
              }}
            >
              {loading ? (
                <Spinner size="sm" />
              ) : (
                <>
                  <FaUpload className="me-2" /> Save Slide
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default CarouselAdmin;
